import * as Select from '@radix-ui/react-select'
import {
  type ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { brokersMembers } from 'rails_routes'

import { getFormattedDate } from '../../../utils'
import { DropdownMultiFilter } from '../../reusable/table/DropdownMultiFilter'
import { SortableHeader } from '../../reusable/table/SortableHeader'
import TanstackTable, { type PaginationMetadata } from '../../reusable/table/TanstackTable'
import { TextFilter } from '../../reusable/table/TextFilter'
import { Toast } from '../../reusable/Toast'
import EnrollmentStatusPill from '../EnrollmentStatusPill'
import { type EnrollmentStatus, type SubmissionMethod, type Task } from '../types'

type BrokerEnrollment = {
  id: string,
  assignee_id: string,
  formatted_member_name: string,
  employer_name: string,
  employer_approved: 'pending' | 'approved' | 'denied' | 'inactive_duplicate' | 'inactive_withdrawn' | 'inactive_ineligible' | 'inactive_churned' | null,
  carrier_name: string,
  plan_state: string,
  coverage: string,
  coverage_start_date: string,
  member_confirmed_at: string,
  merged_status: EnrollmentStatus,
  submission_method: SubmissionMethod,
  member_id: string,
  tasks: Task[],
}

type Broker = {
  id: string | null,
  broker_name: string,
}

type BrokerEnrollmentFilter = {
  formatted_member_name?: string;
  carrier_name?: string;
  employer_name?: string;
  employer_approved?: string;
  merged_status?: string;
  coverage?: string;
  plan_state?: string;
  assignee_name?: string;
  coverage_start_date?: string;
  member_confirmed_at?: string;
}

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<BrokerEnrollment>()

function setUrlParam (key: string, value: any) {
  const urlObj = new URL(window.location.href)

  // Convert the filter object to a JSON string
  const serializedValue = JSON.stringify(value)

  // Check if there's already a `filter` parameter
  if (urlObj.searchParams.has(key)) {
    // If it exists, update the value
    urlObj.searchParams.set(key, serializedValue)
  } else {
    // Otherwise, append the new parameter
    urlObj.searchParams.append(key, serializedValue)
  }

  // Update the browser's current URL without reloading the page
  window.history.replaceState({}, '', urlObj.toString())
}

function getUrlParam (key: string): any {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  if (value) {
    return JSON.parse(value)
  }

  return undefined
}

const AssigneeCell = ({ row, brokers, setSelectedBroker }:
{ row: any, brokers: Record<string, Broker>, setSelectedBroker: (id: string) => void }
) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const assigneeId = row.original.assignee_id
  const broker = brokers[assigneeId]

  const filteredBrokers = [{ id: null, broker_name: 'Unassigned' } as Broker].concat(Object.values(brokers)).filter((broker) =>
    broker.broker_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      if (inputRef.current) {
        inputRef.current.focus()
      }
    } else {
      window.removeEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleSelect = (brokerId: string) => {
    if (brokerId !== broker?.id) {
      setSelectedBroker(brokerId)
    }
    setIsOpen(false)
  }

  return (
    <div className='text-xs bg-white rounded min-w-[130px] max-w-[130px]'>
      <Select.Root open={isOpen} onOpenChange={setIsOpen} onValueChange={handleSelect}>
        <Select.Trigger asChild>
          <button
            aria-label='Customize options'
            className='flex items-center justify-between space-x-2 px-2 py-1 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 min-w-[130px] max-w-[130px]'
          >
            <span className='flex-grow text-left overflow-hidden whitespace-nowrap text-ellipsis text-gray-800'>
              {broker?.broker_name || 'Unassigned'}
            </span>
            <div className='flex-shrink-0 w-4 text-gray-600'>
              <ChevronDown size={16} />
            </div>
          </button>
        </Select.Trigger>
        <Select.Content className='SelectContent' sideOffset={5} position='popper'>
          <div>
            <input
              type='text'
              placeholder='Search...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className='w-full p-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none'
              ref={inputRef}
            />
          </div>
          <div className='max-h-60 overflow-y-auto rounded-lg border border-gray-300 focus:outline-none'>
            {filteredBrokers.map((broker) => (
              <Select.Item
                key={broker.id}
                value={broker.id as string}
                className='p-2 bg-white hover:bg-gray-200 rounded-lg focus:outline-none'
              >
                <span className='text-gray-800'>{broker.broker_name}</span>
              </Select.Item>
            ))}
          </div>
        </Select.Content>
      </Select.Root>

    </div>
  )
}

function BrokersUnprocessedEnrollmentsTable () {
  const [data, setData] = useState<BrokerEnrollment[]>([])
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(getUrlParam('filters') || [])
  const [sorting, setSorting] = useState<SortingState>(getUrlParam('sort') || [])
  const [pagination, setPagination] = useState({
    pageIndex: getUrlParam('pageIndex') || 1,
    pageSize: PAGE_SIZE,
  })

  const [brokers, setBrokers] = useState<Record<string, Broker>>({})

  const columns = [
    columnHelper.accessor('formatted_member_name', {
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='Member' />
      ),
      cell: ({ getValue }) => {
        return (
          <span className='max-w-40 block truncate' title={getValue()}>{getValue()}</span>
        )
      },
      enableSorting: true,
    }),
    columnHelper.accessor('assignee_id', {
      header: 'Assignee',
      cell: ({ row }) => {
        return <AssigneeCell row={row} brokers={brokers} setSelectedBroker={(brokerId) => assignEnrollment(row.original.id, brokerId)} />
      },
    }),
    columnHelper.accessor('employer_name', {
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='Employer' />
      ),
      cell: ({ getValue }) => {
        return (
          <span className='max-w-40 block truncate'>{getValue()}</span>
        )
      },
      enableSorting: true,
    }),
    columnHelper.accessor('employer_approved', {
      header: 'Employer Approved',
      cell: ({ getValue }) => {
        const status = getValue()
        if (!status) return <span className='text-gray-400'>-</span>

        const statusConfig = {
          approved: 'Yes',
          denied: 'No',
          pending: 'Pending',
          inactive_duplicate: 'No - duplicate',
          inactive_withdrawn: 'No - withdrawn',
          inactive_ineligible: 'No - ineligible',
          inactive_churned: 'No - churned'
        }

        return <span className='max-w-40 block truncate'>{statusConfig[status]}</span>
      },
      enableSorting: true,
    }),
    columnHelper.accessor('carrier_name', {
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='Carrier' />
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('plan_state', {
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='State' />
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('coverage_start_date', {
      header: (info) => (
        <div className='whitespace-nowrap'>
          <SortableHeader<BrokerEnrollment> {...info} headerText='Start Date' />
        </div>
      ),
      cell: (props) => (props.getValue() ? getFormattedDate(props.getValue(), { timeZone: 'UTC' }) : 'N/A'),
      enableSorting: true,
    }),
    columnHelper.accessor('member_confirmed_at', {
      header: (info) => (
        <div className='whitespace-nowrap'>
          <SortableHeader<BrokerEnrollment> {...info} headerText='Member Confirmed' />
        </div>
      ),
      cell: (props) => (props.getValue() ? getFormattedDate(props.getValue(), { timeZone: 'UTC' }) : 'N/A'),
      enableSorting: true,
    }),
    columnHelper.accessor('merged_status', {
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='Status' />
      ),
      cell: ({ row }) => {
        // TODO: Reintroduce status tip within the pill component.
        return (
          <EnrollmentStatusPill
            status={row.original.merged_status}
            submissionMethod={row.original.submission_method}
            removeManualSubmissionPrefix
          />
        )
      }
    }),
    columnHelper.accessor('coverage', {
      cell: (props) => (props.getValue().charAt(0).toUpperCase() + props.getValue().slice(1)),
      header: (info) => (
        <SortableHeader<BrokerEnrollment> {...info} headerText='Coverage' />
      ),
      enableSorting: true,
    }),
  ]

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnFilters,
      sorting,
      pagination,
    },
  })

  useEffect(() => {
    const fetchUnprocessedEnrollments = async () => {
      try {
        setLoading(true)
        const url = '/api/web/brokers/brokers'
        // TODO: this method of making an API call is deprecated -- please use reactquery instead
        // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
        const response = await fetch(url, { // eslint-disable-line thatch/no-bare-fetch
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        })
        const json = await response.json()

        if (!response.ok) {
          throw new Error(json.message)
        }
        const brokers = json.data.reduce((acc: Record<string, Broker>, broker: Broker) => {
          acc[broker.id as string] = broker
          return acc
        }, {})
        setBrokers(brokers)
      } catch (err: any) {
        console.error('Error retrieving brokers: ', err.message)
      }

      try {
        const pageIndex = pagination.pageIndex.toString()
        const queryParams: Record<string, any> = {
          'page[number]': pageIndex,
          'page[size]': PAGE_SIZE,
        }

        for (const filter of columnFilters) {
          queryParams[`filter[${filter.id}]`] = filter.value
        }

        queryParams['sort'] = ''
        for (const sort of sorting) {
          queryParams['sort'] += (sort.desc ? '-' : '+') + sort.id
        }

        // make API calls with the query parameter
        const queryString = new URLSearchParams(queryParams).toString()
        const url = `/api/web/brokers/unprocessed_enrollments?${queryString}`
        // TODO: this method of making an API call is deprecated -- please use reactquery instead
        // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
        const response = await fetch(url, { // eslint-disable-line thatch/no-bare-fetch
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        })
        const json = await response.json()

        if (!response.ok) {
          throw new Error(json.message)
        }
        setData(json.data)
        setPaginationMetadata(json.pagination)
      } catch (err: any) {
        console.error('Error retrieving enrollments: ', err.message)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    fetchUnprocessedEnrollments()
  }, [table, pagination.pageIndex, sorting, columnFilters])

  const handleSetColumnFilters = useCallback(
    (filters: ColumnFiltersState) => {
      setColumnFilters(filters)

      setUrlParam('filters', filters)

      // reset the page index to 1 when filters are being applied
      if (pagination.pageIndex !== 1) {
        setPagination({ ...pagination, pageIndex: 1 })
      }
    },
    [pagination]
  )

  // Update the URL parameters when sorting or pagination changes, so a refresh or saved
  // URL will maintain the current sorting.
  useEffect(() => {
    setUrlParam('sort', sorting)
  }, [sorting])

  useEffect(() => {
    setUrlParam('pageIndex', pagination.pageIndex)
  }, [pagination.pageIndex])

  const assignEnrollment = async (enrollmentId: string, brokerId: string) => {
    try {
      const url = `/api/web/brokers/unprocessed_enrollments/${enrollmentId}/assign`
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(url, { // eslint-disable-line thatch/no-bare-fetch
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ broker_id: brokerId }),
      })
      const json = await response.json()

      if (!response.ok) {
        const error = json.message || 'An error occurred'
        toast.error(error)

        throw new Error(json.message)
      }

      const updatedEnrollment = json
      setData((data) =>
        data.map((enrollment: BrokerEnrollment) => (
          enrollment.id === updatedEnrollment.id ? updatedEnrollment : enrollment
        ))
      )
    } catch (err: any) {
      console.error('Error assigning enrollment: ', err.message)
    }
  }

  return (
    <>
      {/* Searchbar */}
      <div className='mb-4 text-xs text-thatch-gray-secondary'>
        To select multiple options, enter comma-separated values (e.g. "CA, TX").
      </div>
      <div className='grid grid-cols-4 gap-3 mb-4'>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='formatted_member_name'
            placeholder='Member name'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
        </div>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='employer_name'
            placeholder='Employer name'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
        </div>
        <div>
          <DropdownMultiFilter<BrokerEnrollmentFilter>
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
            property='employer_approved'
            label='Employer Approved'
            options={[
              { label: 'Yes', value: 'approved' },
              { label: 'No', value: 'denied' },
              { label: 'Pending', value: 'pending' },
              { label: 'No - duplicate', value: 'inactive_duplicate' },
              { label: 'No - withdrawn', value: 'inactive_withdrawn' },
              { label: 'No - ineligible', value: 'inactive_ineligible' },
              { label: 'No - churned', value: 'inactive_churned' },
            ]}
            ariaLabel='Employer approval filter'
          />
        </div>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='assignee_name'
            placeholder='Assignee'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
        </div>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='carrier_name'
            placeholder='Carrier name'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
        </div>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='plan_state'
            placeholder='State'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
          <div className='text-xs text-thatch-gray-secondary'>
            Use the two-letter abbreviation
          </div>
        </div>
        <div>
          <TextFilter<BrokerEnrollmentFilter>
            property='coverage_start_date'
            placeholder='Coverage start date'
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
          />
          <div className='text-xs text-thatch-gray-secondary'>
            Format like "2024-01-01"
          </div>
        </div>
        <div>
          <DropdownMultiFilter<BrokerEnrollmentFilter>
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
            property='merged_status'
            label='Status'
            options={[
              { label: 'Member confirmed', value: 'member_confirmed' },
              { label: 'In progress', value: 'manual_submission_in_progress' },
              { label: 'Blocked', value: 'manual_submission_blocked' },
              { label: 'Awaiting general agent', value: 'manual_submission_awaiting_general_agent' },
              { label: 'Awaiting member', value: 'manual_submission_awaiting_member' },
              { label: 'Binder payment required', value: 'binder_payment_required' },
              { label: 'Autopay setup required', value: 'autopay_setup_required' },
              { label: 'Completed', value: 'completed' },
            ]}
            ariaLabel='Status filter'
          />
        </div>
        <div>
          <DropdownMultiFilter<BrokerEnrollmentFilter>
            columnFilters={columnFilters}
            setColumnFilters={handleSetColumnFilters}
            property='coverage'
            label='Coverage type'
            options={[
              { label: 'Medical', value: 'medical' },
              { label: 'Dental', value: 'dental' },
              { label: 'Vision', value: 'vision' },
            ]}
            ariaLabel='Coverage filter'
          />
        </div>
      </div>
      {/* Table */}
      <TanstackTable
        table={table}
        isLoading={loading}
        isError={error}
        noDataMessage='No transactions found'
        tableMessageHeightClass='h-16'
        paginationLabelSingular='incomplete enrollment'
        paginationMetadata={paginationMetadata}
        setSorting={setSorting}
        onNextPageClick={() =>
          setPagination({ ...pagination, pageIndex: paginationMetadata?.next_page || 1 })}
        onPreviousPageClick={() =>
          setPagination({ ...pagination, pageIndex: paginationMetadata?.prev_page || 1 })}
        onRowClick={({ row }) => {
          const memberId = row.original.member_id

          if (memberId) {
            window.open(brokersMembers.show({ id: memberId }), '_blank')
          }
        }}
      />
      <Toast />
    </>
  )
}

export default BrokersUnprocessedEnrollmentsTable
